
import { BatchData } from '@/models/batch-data.model';
import { computed, defineComponent, PropType, ref, toRef } from 'vue';
import { rules } from '@/store/stateless/rules';
import { storeToRefs } from 'pinia';
import { apiClient } from '@/lib/api/client';
import { CouponsWithImageUrl, ValidateCouponResponse } from '@/lib/api/coupons.api';
import { CouponRejectionReasons } from '@/lib/api/coupon-approvals.api';
import { useCouponStore } from '@/store/useCoupon';
import { useScannerCouponStore } from '@/store/useScannerCoupon';
import { useCouponTypesStore } from '@/store/useCouponTypes';

export default defineComponent({
  name: 'one-coupon',
  props: {
    item: {
      required: true,
      type: Object as PropType<CouponsWithImageUrl>,
    },
    batchData: {
      type: Object as PropType<BatchData>,
      default: {} as BatchData,
    },
    batchId: {
      type: String,
    }
  },
  setup(props) {
    const coupon = useCouponStore();
    const scannerCoupon = useScannerCouponStore();
    const couponTypesStore = useCouponTypesStore();

    const { currentScanner } = storeToRefs(scannerCoupon);

    const batchData = toRef(props, 'batchData');
    const item = toRef(props, 'item');

    const couponFields = ref(null);
    const couponImage = ref(null);
    const cropping = ref(false);
    const customReason = ref('');

    const isOthers = ref(false);
    const loading = ref(false);
    const loadingError = ref(false);
    const approvalError = ref<string | null>(null);
    const resultValidateCoupon = ref<ValidateCouponResponse | null>(null);
    const updateUrl = ref(null);
    const valid = ref(false);
    const version = ref(0);

    const transferElement = (element) => {
      [element.object.type] = element.text;
    };

    const vehicleSelected = computed(() => {
      if (!currentScanner.value.isAppMembre) {
        return batchData.value.batchId;
      }

      return true;
    });

    const isProcessed = computed(() => {
      return (
        item.value.state === 'rejected' ||
        (item.value.state === 'approved' && item.value.importState === 'odoo-import-success')
      );
    });

    const cropperClosed = async (reload: boolean) => {
      cropping.value = false;
      version.value += 1;

      if (reload) {
        await scannerCoupon.refetchByCouponId(item.value.id);
      }
    };

    const approve = async () => {
      // Get coupon type from store to check code
      const couponType = couponTypesStore.getTypeById(couponFields.value.couponTypeId);

      if (
        couponType?.code === 'CHO' &&
        couponFields.value.accountNumber.toString().at(0) !== 'H'
      ) {
        couponFields.value.accountNumber = `H${couponFields.value.accountNumber}`;
      }

      if (valid.value) {
        try {
          loading.value = true;
          approvalError.value = null;

          const { data, response } = await apiClient.POST('/coupons/validate', {
            body: {
              accountNumber: couponFields.value.accountNumber,
              couponNumber: couponFields.value.couponNumber,
              couponTypeId: couponFields.value.couponTypeId,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to validate coupon');
          }

          resultValidateCoupon.value = data;
          if (!data.success) {
            return;
          }

          const approval = await coupon.createCouponApproval();
          await coupon.saveApproval(item.value.id, approval.id, "approved");
          await coupon.importCouponInOdoo(item.value.id);
          await scannerCoupon.refetchByCouponId(item.value.id);
          await scannerCoupon.refreshAllScannerCounts();
        } catch (error: any) {
          const msg = error?.response?.data?.message ?? error?.message;
          if (msg) {
            approvalError.value = msg;
          }
          console.log(error);
        } finally {
          loading.value = false;
        }
      }

      loading.value = false;
      return null;
    };

    const refuse = async (reasonCode: CouponRejectionReasons) => {
      if (reasonCode === 'other') {
        isOthers.value = true;
      } else {
        await handleRefuse(reasonCode);
      }
    };

    const handleRefuse = async (reasonCode: CouponRejectionReasons) => {
      try {
        loadingError.value = true;

        if (isOthers.value === true) {
          if (customReason.value.length > 30) {
            throw new Error(
              'Le motif de rejet personnalisé est obligatoire et ne doit pas comporter plus de 30 caractères',
            );
          }

          if (customReason.value.length === 0) {
            throw new Error('Ce champ est obligatoire');
          }
        }

        const approval = await coupon.rejectCoupon(reasonCode, customReason.value);
        await coupon.saveApproval(item.value.id, approval.id, "rejected");
        await scannerCoupon.refetchByCouponId(item.value.id);
        await scannerCoupon.refreshAllScannerCounts();
        isOthers.value = false;
      } catch (error) {
        console.log(error);
      } finally {
        loadingError.value = false;
      }
    };

    return {
      approvalError,
      customReason,
      isOthers,
      version,
      updateUrl,
      loading,
      loadingError,
      couponImage,
      currentScanner,
      cropping,
      valid,
      couponFields,
      resultValidateCoupon,
      vehicleSelected,
      isProcessed,
      cropperClosed,
      transferElement,
      approve,
      refuse,
      handleRefuse,
      rules,
    };
  },
});
